import { useParams } from "react-router-dom";
import { useEffect, useState, useContext, useRef, useMemo } from "react";

import Header from "../../../Components/Header";
import Footer from "../../../Components/Footer/";
import axios from "axios";

import { TradeHeader } from "./TradeHeader.js";
import { BuySellOrder } from "./BuySellOrder";
import { OrderBook } from "./OrderBook";
import { RecentTrades } from "./RecentTrades";
import { Portfolio } from "./Portfolio";
import {
  VolumeData,
  IncreasingData,
  DecreasingData,
} from "../../../Common/DummyData";
import { useNavigate } from "react-router-dom";
import { CoinContext } from "../../../Context/CoinContext";
import { useModalContext } from "../../../Context/ModalContext";
import { CoinSlider } from "../../../Components/CoinSlider/CoinSlider";
import { useSelector } from "react-redux";
import { useWebSocket } from "../../../Context/WebSocketContext.js";
import { formatNumberWithComma } from "../../../Common/Helpers";
import DepthChart from "./DepthChart";
import datafeed from "./datafeed";
import _ from "lodash";
import { useDispatch } from "react-redux";
import { setLatestTradePrice } from "../../../Features/Exchange/ExchangeSlice";
import { useTrading } from "../../../Hooks/useTrading";
import axiosInstance from "../../../Utils/axiosConfig.js";
import { PageVisible } from "../../../constants/PageVisible.js";

function Trade() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pair } = useParams();
  let currentPair = pair;

  // Get labels from Redux
  const labels = useSelector((state) => state.labels.data);
  const { crypto_price, fiat_price } = useSelector((state) => state.price);

  // Get coin context
  const {
    primaryCoin,
    setPrimaryCoin,
    newCoin,
    setNewCoin,
    MARKET,
    SETMARKET,
    activeMarket,
    setActiveMarket,
    setCoinsData,
    defaultPair,
  } = useContext(CoinContext);

  // Get modal context
  const {
    selectedNumberFormat,
    selectedLanguage,
    selectedFiatEstimation,
    selectedTheme,
  } = useModalContext();

  // ✅ NEW: Get WebSocket data using useWebSocket hook
  const {
    MKData,
    coinsData,
    pairs,
    userOpenOrders, // User's open orders (all pairs)
    isLoggedIn,
    isConnected,
  } = useWebSocket();

  // ✅ NEW: Use useTradingPage hook for automatic OB, RT subscriptions
  // This automatically subscribes to OB.{pair}, RT.{pair} when component mounts
  // and unsubscribes when component unmounts or pair changes
  const {
    asks,
    bids,
    trades,
    latestTradePrice,
    latestTradeType,
    latestPrices,
  } = useTrading(newCoin, activeMarket, "1");

  // Local state
  const [sliderData, setSliderData] = useState(MKData);
  const [searchWord, setSearchWord] = useState();
  const [increasingData, setIncreasingData] = useState(IncreasingData);
  const [decreasingData, setDecreasingData] = useState(DecreasingData);
  const [volumeData, setVolumeData] = useState(VolumeData);
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [tableShowData, setTableShowData] = useState([]);
  const [lastPrice, setLastPrice] = useState(null);
  const [averageBuyPrice, setAverageBuyPrice] = useState(null);
  const [averageSellPrice, setAverageSellPrice] = useState(null);
  const [transactions, setTransactions] = useState();
  const [openOrders, setOpenOrders] = useState([]);
  const [averageOfLatestPrices, setAverageOfLatestPrices] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPair, setSelectedPair] = useState();

  // Update slider data when MKData changes
  useEffect(() => {
    setSliderData(MKData);
  }, [MKData]);

  // Handle URL routing and pair updates
  useEffect(() => {
    const pathname = window.location.pathname;
    const slashCount = pathname.split("/").length - 1;

    if (pathname === "/trade/") {
      window.history.replaceState({}, "", `/trade`);
    } else if (slashCount === 1 && newCoin && MARKET) {
      const pair = `${newCoin}-${MARKET}`;
      window.history.replaceState({}, "", `/trade/${pair}`);
    } else if (slashCount === 2) {
      const pairString = pathname.split("/")[2];
      const [base, quote] = pairString.split("-");

      setNewCoin(base.toUpperCase());
      SETMARKET(quote.toUpperCase());
      setActiveMarket(quote.toUpperCase());
    }
  }, [
    navigate,
    newCoin,
    MARKET,
    activeMarket,
    setNewCoin,
    SETMARKET,
    setActiveMarket,
  ]);

  // Calculate asks with cumulative totals
  const calculatedAsks = useMemo(() => {
    return asks?.map((ask, index) => {
      const total = asks
        ?.slice(0, index + 1)
        .reduce((sum, ask) => sum + ask[1], 0);
      return {
        price: ask[0],
        size: formatNumberWithComma(ask[1], selectedNumberFormat),
        total: formatNumberWithComma(total, selectedNumberFormat),
      };
    });
  }, [asks, selectedNumberFormat]);

  // Calculate bids with cumulative totals
  const calculatedBids = useMemo(() => {
    return bids?.map((bid, index) => {
      const total = bids
        ?.slice(0, index + 1)
        .reduce((sum, bid) => sum + bid[1], 0);
      return {
        price: bid[0],
        size: formatNumberWithComma(bid[1], selectedNumberFormat),
        total: formatNumberWithComma(total, selectedNumberFormat),
      };
    });
  }, [bids, selectedNumberFormat]);

  const [convertedPrices, setConvertedPrices] = useState([]);
  const [average, setAverage] = useState(0);
  const [currentCoinRate, setCurrentCoinRate] = useState();
  const [currentCurrencyRate, setCurrentCurrencyRate] = useState();
  const [allCurrencyRates, setAllCurrencyRates] = useState([]);

  useEffect(() => {
    if (crypto_price?.data?.rateList && newCoin) {
      const priceList = crypto_price.data.rateList;
      const currentRate = priceList.find((item) => item.currency === newCoin);
      setCurrentCoinRate(currentRate?.rate);
    }
  }, [crypto_price, newCoin]);

  useEffect(() => {
    if (selectedFiatEstimation?.name && fiat_price?.data?.rateList) {
      const rateList = fiat_price.data.rateList;
      const rateData = rateList.find(
        (rate) => rate.currency === selectedFiatEstimation.name
      );
      setCurrentCurrencyRate(rateData?.rate || null);
    }
  }, [selectedFiatEstimation?.name, fiat_price]);

  useEffect(() => {
    if (!currentCoinRate) return;

    if (
      selectedFiatEstimation?.name &&
      latestPrices?.length > 0 &&
      selectedFiatEstimation?.name !== "USD" &&
      currentCurrencyRate
    ) {
      const newConvertedPrices = latestPrices.map(
        (price) => price * currentCurrencyRate
      );
      setConvertedPrices(newConvertedPrices);
      setAverage(currentCoinRate * currentCurrencyRate);
    } else if (
      selectedFiatEstimation?.name === "USD" &&
      latestPrices?.length > 0
    ) {
      setAverage(currentCoinRate * 1);
    }
  }, [
    currentCoinRate,
    currentCurrencyRate,
    selectedFiatEstimation?.name,
    latestPrices,
  ]);

  const filterTableData = (pair) => {
    if (!MKData || !pair) return [];

    let filteredArray = MKData.filter((item) => item.quote === pair);

    filteredArray.sort((a, b) => b.price - a.price);
    setTableShowData(filteredArray);

    const sortedByVolume = [...filteredArray].sort(
      (a, b) => b.base_volume - a.base_volume
    );
    setVolumeData(sortedByVolume.slice(0, 3));

    const sortedByChange = [...filteredArray].sort(
      (a, b) => b.change_in_price - a.change_in_price
    );
    setIncreasingData(
      sortedByChange.filter((item) => item.change_in_price > 0).slice(0, 5)
    );

    setDecreasingData(sortedByChange.slice(-3));
  };

  useEffect(() => {
    if (!isSearching) {
      if (selectedPair) {
        filterTableData(selectedPair);
      } else {
        filterTableData(defaultPair);
      }
    }
  }, [selectedPair, defaultPair, MKData, isSearching]);

  const handleItemClick = (item) => {
    setSelectedPair(item);
    SETMARKET(item);
  };

  const coin = coinsData?.find(
    (item) => item.base === newCoin && item.quote === activeMarket
  );

  const getBalance = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
    const reqBody = { currency: "ALL" };

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_REST_URL}/api/GetBalance`,
        reqBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const [userBalanceArray, setUserBalanceArray] = useState();
  const [tradeCompleted, setTradeCompleted] = useState(false);

  // Get user balance on mount and after trade completion
  const getUserBalance = async () => {
    let userBalance = await getBalance();
    if (userBalance) {
      const userBalanceArr = userBalance.data?.data;
      setUserBalanceArray(userBalanceArr);
    }
  };

  useEffect(() => {
    getUserBalance();
  }, [tradeCompleted]);

  // Get specific balances for current pair
  const pairBalance = userBalanceArray?.find(
    (item) => item.currency === activeMarket
  );
  const coinBalance = userBalanceArray?.find(
    (item) => item.currency === coin?.base
  );

  // TradingView chart setup
  const symbol = `${newCoin}/${activeMarket}`;
  const tvWidgetRef = useRef(null);
  const [isChartLoading, setIsChartLoading] = useState(false);

  useEffect(() => {
    setIsChartLoading(true);

    // Clean up previous widget
    if (tvWidgetRef.current !== null) {
      tvWidgetRef.current.remove();
    }

    // Widget options
    const widgetOptions = {
      symbol: symbol,
      interval: "60",
      container: "tv_chart_container",
      debug: false,
      datafeed: datafeed,
      library_path: "/charting_library/charting_library/",
      locale: selectedLanguage?.code,
      enabled_features: [],
      disabled_features: [
        "use_localstorage_for_settings",
        "header_saveload",
        "header_symbol_search",
        "symbol_search_hot_key",
        "timeframes_toolbar",
        "header_compare",
      ],
      charts_storage_url: "http://saveload.tradingview.com",
      charts_storage_api_version: "1.1",
      client_id: "tradingview.com",
      user_id: "public_user_id",
      theme: selectedTheme?.name,
    };

    // Create widget
    if (!tvWidgetRef.current) {
      const tvWidget = new window.TradingView.widget(widgetOptions);

      tvWidget.onChartReady(() => {
        setIsChartLoading(false);
      });

      tvWidgetRef.current = tvWidget;
    }

    // Timeout fallback
    const timeout = setTimeout(() => {
      if (isChartLoading) {
        setIsChartLoading(false);
      }
    }, 10000);

    // Cleanup
    return () => {
      clearTimeout(timeout);
      if (tvWidgetRef.current !== null) {
        tvWidgetRef.current.remove();
        tvWidgetRef.current = null;
      }
    };
  }, [newCoin, activeMarket, selectedTheme, selectedLanguage]);

  // Depth chart view mode
  const [activeViewMode, setActiveViewMode] = useState("chart");

  // Get depth chart data from order book
  const getDepthChartData = (orderBook, type) => {
    let newOrderBook = orderBook.map((item) => ({
      price: parseFloat(item.price) || 0,
      volume: parseFloat(item.size) || 0,
    }));

    let sortedOrderBook = _.orderBy(
      newOrderBook,
      "price",
      type === "ask" ? "asc" : "desc"
    );

    let cumulativeVolume = 0;

    sortedOrderBook = sortedOrderBook.map((singleItem, i) => {
      cumulativeVolume += singleItem.volume;

      return {
        ...singleItem,
        [`${type}TotalVolume`]: cumulativeVolume,
      };
    });

    return sortedOrderBook;
  };

  const [depthChartData, setDepthChartData] = useState([]);
  const tradingPair = { baseCurrency: newCoin, quoteCurrency: activeMarket };

  // Update depth chart when order book changes
  useEffect(() => {
    updateDepthChart();
  }, [calculatedAsks, calculatedBids]);

  const updateDepthChart = () => {
    const depthChartAsk = getDepthChartData(calculatedAsks, "ask");
    const depthChartBid = getDepthChartData(calculatedBids, "bid");

    setDepthChartData(_.sortBy([...depthChartBid, ...depthChartAsk], "price"));
  };

  // Update Redux with latest trade price
  useEffect(() => {
    if (coin?.price) {
      dispatch(setLatestTradePrice(coin.price));
    }
  }, [coin, primaryCoin, dispatch]);

  return (
    <>
      <Header
        coin={coin}
        primaryCoin={primaryCoin}
        dropdownVisible={dropdownVisible}
        setDropdownVisible={setDropdownVisible}
        pairs={pairs}
        selectedPair={selectedPair}
        handleItemClick={handleItemClick}
        tableShowData={tableShowData}
        searchWord={searchWord}
        MARKET={MARKET}
        SETMARKET={SETMARKET}
      />

      {PageVisible.showPriceBand && <CoinSlider data={sliderData} />}

      <TradeHeader
        latestTradeType={latestTradeType}
        setActiveMarket={setActiveMarket}
        coinsData={coinsData}
        setCoinsData={setCoinsData}
        coin={coin}
        primaryCoin={primaryCoin}
        dropdownVisible={dropdownVisible}
        setDropdownVisible={setDropdownVisible}
        pairs={pairs}
        selectedPair={selectedPair}
        handleItemClick={handleItemClick}
        tableShowData={tableShowData}
        setTableShowData={setTableShowData}
        searchWord={searchWord}
        setSearchWord={setSearchWord}
        increasingData={increasingData}
        isSearching={isSearching}
        setIsSearching={setIsSearching}
      />

      <div className={dropdownVisible ? "row blured" : "row"}>
        <div className="col-lg-8 pe-lg-0">
          <div className="d-flex gap-2 trading-switcher-wrapper">
            <button
              className={activeViewMode === "chart" ? "active" : ""}
              onClick={() => setActiveViewMode("chart")}
            >
              {labels?.exchange?.chart}
            </button>
            <button
              className={activeViewMode === "depth" ? "active" : ""}
              onClick={() => setActiveViewMode("depth")}
            >
              {labels?.exchange?.depthChart?.title}
            </button>
          </div>

          <div
            id="tv_chart_container"
            style={{
              display:
                isChartLoading || activeViewMode === "depth" ? "none" : "block",
            }}
          />

          <div
            id="depth-chart"
            style={{
              display: activeViewMode === "chart" ? "none" : "block",
              width: "100%",
              minHeight: "400px",
            }}
          >
            {activeViewMode === "depth" && (
              <DepthChart
                tradingPair={tradingPair}
                depthChartData={depthChartData}
              />
            )}
          </div>
          <Portfolio
            tradeCompleted={tradeCompleted}
            setTradeCompleted={setTradeCompleted}
            coin={coin}
          />
        </div>

        <div className="col-lg-2 border-start border-end custom-border-color border-secondary px-lg-0">
          <div className="pb-3 pt-2 px-0 order-book-container">
            <OrderBook
              asks={calculatedAsks}
              bids={calculatedBids}
              coin={coin}
              primaryCoin={primaryCoin}
              latestTradeType={latestTradeType}
              latestTradePrice={latestTradePrice}
              averageOfLatestPrices={average}
            />
            <RecentTrades
              coinsData={coinsData}
              coin={coin}
              primaryCoin={primaryCoin}
              MARKET={MARKET}
              activeMarket={activeMarket}
            />
          </div>
        </div>
        <div className="col-lg-2 ps-lg-0">
          <BuySellOrder
            tradeCompleted={tradeCompleted}
            setTradeCompleted={setTradeCompleted}
            market={MARKET}
            activeMarket={activeMarket}
            coin={newCoin}
            coinPrice={coin?.price}
            pairBalance={pairBalance?.balance}
            coinBalance={coinBalance?.balance}
          />
        </div>
      </div>

      <div className={dropdownVisible ? "row blured" : "row"}>
        <Footer />
      </div>
    </>
  );
}

export default Trade;
